import React, { useEffect, useState, useRef, useContext } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

import moment from "moment";
import * as Icon from "react-feather";

import { H5, P } from "../../../atoms/Texts";
import ActionPageHero from "../../../molecules/ActionPageHero";

import { Table } from "../../../organisms/Table";
import { Pill } from "../../../atoms/Badges/badge.ssn";
import pillStylesEnum from "../../../../shared/fixtures/pillStylesEnum";
import { demoContext } from "../../../../shared/store/contexts";

import Joyride from "react-joyride";
import { useWalkthrough } from "../../../../shared/hooks/useWalkthrough";

const More = ({ serviceId }) => {
  const { id: orgId } = useParams();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="More options"
      >
        <Icon.MoreVertical size={24} color="#C5C7CD" className="cursor-pointer" />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className=" !w-[150px] absolute z-50 top-0 right-0 mt-1 bg-white shadow-lg rounded-md py-2 px-4"
        >
          <Link
            to={`/dashboard/organization/${orgId}/case-management/${serviceId}`}
            className=" w-full flex items-center px-2 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-150"
          >
            <span className="flex-1  whitespace-nowrap block">View Case</span>
            <Icon.ExternalLink className="ml-1" size={12} />
          </Link>
        </div>
      )}
    </div>
  );
};

const AllCasesPage = () => {
  const { cases } = useContext(demoContext);

  const [currentPage, setCurrentPage] = useState(0);

  const steps = [
    {
      target: ".case-header",
      content: "Manage all cases for your organisation here.",
    },
    {
      target: ".case-status",
      content: "This shows the status of your case, whether it is open or closed.",
    },
    {
      target: ".view-case",
      content: "Click here to view a case in details.",
    },
  ];

  const { joyrideProps } = useWalkthrough({
    steps,
    walkthroughKey: "all_cases_page",
    pageSpecific: true, // Each page tracks separately
  });

  return (
    <>
      <Joyride
        key={joyrideProps.run ? "walkthrough-active" : "walkthrough-inactive"}
        {...joyrideProps}
      />

      <main className="flex flex-col w-full">
        <ActionPageHero
          className="organization-container "
          title="Case Management"
          description="Manage all cases"
          action
        />
        <section className="organization-container pb-10 flex-grow flex flex-col mt-6">
          <div className="flex items-center justify-between mb-4">
            <H5 className="case-header  w-fit ">All Cases</H5>
          </div>
          <Table
            columns={columns}
            data={cases}
            loading={false}
            empty_sub_message="Add the case you want to track quickly "
            current_page={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </section>
      </main>
    </>
  );
};

const columns = [
  {
    Header: "No",
    Cell: ({ row }) => (
      <P color="#252733" className=" flex items-center">
        {row.index + 1}
      </P>
    ),
  },
  {
    accessor: "referral",
    Header: "Name",
    Cell: ({ value }) => (
      <P color="#252733" className=" flex items-center">
        {value?.user?.firstName} {value?.user?.lastName} Jane Doe
      </P>
    ),
  },
  {
    accessor: "caseNumber",
    Header: "Case Number",
    Cell: ({ value }) => (
      <P color="#252733" className="">
        {value}
      </P>
    ),
  },
  {
    accessor: "referral.from",
    Header: "Referral Source",
    Cell: ({ value }) => (
      <Pill
        color={pillStylesEnum[value?.toUpperCase()].color}
        bg={pillStylesEnum[value?.toUpperCase()].bg}
        fontSize="12px"
      >
        {value?.toUpperCase()}
      </Pill>
    ),
  },
  {
    accessor: "createdAt",
    Header: "Date",
    Cell: ({ value }) => (
      <div>
        <p className="">{moment(value).format("ll")}</p>
        <P color="#C5C7CD" className="">
          {moment(value).format("LT")}
        </P>
      </div>
    ),
  },
  {
    accessor: "status",
    Header: "Status",
    Cell: ({ value }) => (
      <Pill
        color={pillStylesEnum[value]?.color}
        bg={pillStylesEnum[value]?.bg}
        fontSize="12px"
        className="uppercase case-status"
      >
        {value}
      </Pill>
    ),
  },
  {
    accessor: "id",
    Header: "",
    Cell: ({ value }) => (
      <span className="view-case">
        <More serviceId={value} />
      </span>
    ),
  },
];

export default AllCasesPage;
